/**
 * Admin Logs Page
 * 
 * Two tabs:
 * - Lead Routing: existing allocation log viewer
 * - Activity: system-wide activity log viewer
 */

import { useState, useMemo } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAllocationLogs } from "@/contexts/AllocationLogContext";
import { useActivityLogs } from "@/hooks/useActivityLogs";
import { DateRangeFilter, type DateRangeValue, type PresetKey } from "@/components/DateRangeFilter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Trash2, Filter, Info, RefreshCw } from "lucide-react";

const EVENT_TYPE_COLORS: Record<string, string> = {
  payment_link_created: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  payment_completed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  contract_created: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
  contract_recovered: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  agent_signup: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  contract_claimed: "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200",
  lead_routed: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200",
  webhook_received: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
};

const EVENT_TYPES = [
  "payment_link_created", "payment_completed", "contract_created",
  "contract_recovered", "agent_signup", "contract_claimed",
  "lead_routed", "webhook_received",
];

function ActivityTab() {
  const [eventFilter, setEventFilter] = useState("all");
  const [dateRange, setDateRange] = useState<DateRangeValue | undefined>(undefined);
  const [datePreset, setDatePreset] = useState<PresetKey | undefined>(undefined);
  const { logs, loading, refetch } = useActivityLogs(eventFilter, dateRange);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-4 items-end">
            <div className="w-56">
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                Event Type
              </label>
              <Select value={eventFilter} onValueChange={setEventFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  {EVENT_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={refetch}>
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
              Date Range
            </label>
            <DateRangeFilter
              value={dateRange}
              onChange={setDateRange}
              activePreset={datePreset}
              onPresetChange={setDatePreset}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Activity Events</CardTitle>
          <CardDescription>
            {loading ? "Loading..." : `${logs.length} events${!dateRange ? " (latest 200)" : ""}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!loading && logs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No activity logs yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="font-mono text-xs whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${EVENT_TYPE_COLORS[log.event_type] || "bg-muted text-muted-foreground"}`}>
                        {log.event_type.replace(/_/g, " ")}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {log.actor_email || log.actor_id || "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="text-muted-foreground">{log.entity_type}:</span>{" "}
                      <span className="font-mono text-xs">{log.entity_id.slice(0, 20)}{log.entity_id.length > 20 ? "…" : ""}</span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[250px] truncate">
                      {log.metadata && Object.keys(log.metadata).length > 0
                        ? JSON.stringify(log.metadata).slice(0, 100)
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminLogs() {
  const { state, actions } = useAllocationLogs();
  const [resultFilter, setResultFilter] = useState<"all" | "success" | "rejected">("all");
  const [leadTypeFilter, setLeadTypeFilter] = useState<string>("all");
  const [routingDateRange, setRoutingDateRange] = useState<DateRangeValue | undefined>(undefined);
  const [routingDatePreset, setRoutingDatePreset] = useState<PresetKey | undefined>(undefined);

  const uniqueLeadTypes = useMemo(() => {
    const types = new Set(state.logs.map(l => l.leadTypeName));
    return Array.from(types);
  }, [state.logs]);

  const filteredLogs = useMemo(() => {
    return state.logs.filter(log => {
      if (resultFilter !== "all" && log.result !== resultFilter) return false;
      if (leadTypeFilter !== "all" && log.leadTypeName !== leadTypeFilter) return false;
      if (routingDateRange) {
        const logTime = log.timestamp;
        if (Number(logTime) < routingDateRange.from.getTime() || Number(logTime) > routingDateRange.to.getTime()) return false;
      }
      return true;
    });
  }, [state.logs, resultFilter, leadTypeFilter, routingDateRange]);

  const handleClearLogs = () => {
    actions.clearLogs();
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">System Logs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Lead routing decisions and system activity
          </p>
        </div>

        <Tabs defaultValue="routing">
          <TabsList>
            <TabsTrigger value="routing">Lead Routing</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="routing">
            <div className="space-y-4">
              {/* Clear button */}
              <div className="flex justify-end">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="sm" disabled={state.logs.length === 0}>
                      <Trash2 className="h-4 w-4 mr-2" /> Clear Logs
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Clear all allocation logs?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently remove all {state.logs.length} log entries.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleClearLogs}>Clear All</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              {/* Filters */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Filter className="h-4 w-4" /> Filters
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-4">
                    <div className="w-48">
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Result</label>
                      <Select value={resultFilter} onValueChange={(v) => setResultFilter(v as typeof resultFilter)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Results</SelectItem>
                          <SelectItem value="success">Success Only</SelectItem>
                          <SelectItem value="rejected">Rejected Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-48">
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Lead Type</label>
                      <Select value={leadTypeFilter} onValueChange={setLeadTypeFilter}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          {uniqueLeadTypes.map(type => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                      Date Range <span className="text-muted-foreground/60">(session data only)</span>
                    </label>
                    <DateRangeFilter
                      value={routingDateRange}
                      onChange={setRoutingDateRange}
                      activePreset={routingDatePreset}
                      onPresetChange={setRoutingDatePreset}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Log Entries</CardTitle>
                  <CardDescription>
                    Showing {filteredLogs.length} of {state.logs.length} entries
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredLogs.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      {state.logs.length === 0
                        ? "No allocation logs yet. Use the Lead Simulator to generate activity."
                        : "No logs match the current filters."}
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Timestamp</TableHead>
                          <TableHead>Lead Type</TableHead>
                          <TableHead>State</TableHead>
                          <TableHead>Result</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead className="text-right">Charged</TableHead>
                          <TableHead className="text-right">
                            <span className="flex items-center justify-end gap-1">
                              Score
                              <Tooltip>
                                <TooltipTrigger><Info className="h-3 w-3 text-muted-foreground" /></TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="text-xs">Final allocation score based on quality and delivery pressure.</p>
                                </TooltipContent>
                              </Tooltip>
                            </span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredLogs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell className="font-mono text-xs">
                              {new Date(log.timestamp).toLocaleString()}
                            </TableCell>
                            <TableCell className="font-medium">{log.leadTypeName}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-mono">{log.leadState}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant={log.result === "success" ? "default" : "destructive"}>{log.result}</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                              {log.rejectionReason || "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {log.chargedAmount ? `$${log.chargedAmount}` : "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {log.priorityScore ? (
                                log.allocationBreakdown ? (
                                  <Tooltip>
                                    <TooltipTrigger className="cursor-help underline decoration-dotted underline-offset-2">
                                      {log.priorityScore.toFixed(2)}
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                      <div className="space-y-1.5 text-xs">
                                        <p className="font-semibold border-b pb-1">Allocation Breakdown</p>
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                                          <span className="text-muted-foreground">Quality Score:</span>
                                          <span className="font-mono">{log.allocationBreakdown.qualityScore.toFixed(2)}</span>
                                          <span className="text-muted-foreground">Quality Weight:</span>
                                          <span className="font-mono">{Math.round(log.allocationBreakdown.qualityWeight * 100)}%</span>
                                        </div>
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                ) : log.priorityScore.toFixed(2)
                              ) : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="activity">
            <ActivityTab />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
